import {
  AutoAwesome as AIIcon,
  DriveFolderUpload as DriveFolderUploadIcon,
  PermMedia as PermMediaIcon,
  PhotoCameraOutlined as PhotoCameraOutlinedIcon,
  PhotoFilter as PhotoFilterIcon,
  Tag as TagIcon,
} from '@mui/icons-material';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useThrottle from 'src/utils/throttle/throttle.component';
import EmojiPicker from 'src/components/EmojiPicker';

const PostDescription = ({
  handleChangeDescription,
  valueDescription,
  handleUploadMediaPopup,
  handleBulkMediaPopup,
  showInputProps,
  handleAIContent,
  handleGenerateHashtags,
  AIHashtagText,
  handleImageGeneration,
  handlePhotoEditor,
}) => {
  const theme = useTheme();
  const isXlDown = useMediaQuery(theme.breakpoints.down('xl'));
  const { t: translate } = useTranslation();

  // State Variables
  const [description, setDescription] = useState('');

  useEffect(() => {
    setDescription(valueDescription);
  }, [valueDescription]);

  const throttledChangeDescription = useThrottle((value) => {
    handleChangeDescription(value);
  }, 500);

  const handleDescription = (value) => {
    setDescription(value);
    throttledChangeDescription(value);
  };
    const handleEmoji = (value) => {
    throttledChangeDescription(description + value);
  };

  return (
    <>
      <Box position="relative" style={{ paddingTop: showInputProps && '1rem' }}>
        <Typography style={{ color: theme.palette.text.secondary, ...theme.typography.subtitle2 }}>
          {translate('post_description.description', 'Description')}
        </Typography>
        {showInputProps ? (
          <TextField
            name="description"
            placeholder={translate('post_description.enter_post_content', 'Enter Post Content')}
            fullWidth
            multiline
            rows={showInputProps ? 6 : 4}
            onChange={(event) => {
              handleDescription(event.target.value);
            }}
            value={description}
            sx={{
              bgcolor: 'background.neutral',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'transparent',
                },
              },
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              borderBottomLeftRadius: !showInputProps && 8,
              borderBottomRightRadius: !showInputProps && 8,
              borderBottom: showInputProps
                ? `1px solid ${theme.palette.background.neutral}`
                : `1px solid ${theme.palette.text.border}`,
              borderTop: `1px solid ${theme.palette.text.border}`,
              borderLeft: `1px solid ${theme.palette.text.border}`,
              borderRight: `1px solid ${theme.palette.text.border}`,
            }}
          />
        ) : (
          <textarea
            placeholder={translate('post_description.enter_post_content', 'Enter Post Content')}
            rows="4"
            style={{
              width: '100%',
              backgroundColor: theme.palette.background.neutral,
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              borderBottomLeftRadius: showInputProps ? '0' : '8px',
              borderBottomRightRadius: showInputProps ? '0' : '8px',
              borderTop: `1px solid ${theme.palette.text.border}`,
              borderLeft: `1px solid ${theme.palette.text.border}`,
              borderRight: `1px solid ${theme.palette.text.border}`,
              borderBottom: showInputProps
                ? `1px solid ${theme.palette.background.neutral}`
                : `1px solid ${theme.palette.text.border}`,
              outline: 'none',
              padding: '1rem',
              fontFamily: 'sans-serif',
              fontSize: '1rem',
            }}
            onChange={(event) => handleDescription(event.target.value)}
            value={description}
          />
        )}
        {showInputProps && (
          <Box
            sx={{
              bottom: '7rem',
              left: '1.5rem',
              right: '1.5rem',
              display: 'flex',
              backgroundColor: 'white',
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
              border: '1px solid',
              borderTopColor: 'background.neutral',
              borderBottomColor: theme.palette.text.border,
              borderLeftColor: theme.palette.text.border,
              borderRightColor: theme.palette.text.border,
              // mb: 2,
            }}
          >
            <Tooltip title={translate('post_description.upload_media', 'Upload Media')}>
              <IconButton
                onClick={() => {
                  handleUploadMediaPopup(true);
                }}
              >
                <PhotoCameraOutlinedIcon style={{ fontSize: '1.5rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={translate('post_description.edit_photo_upload', 'Edit Photo to Upload')}>
              <IconButton
                onClick={() => {
                  handlePhotoEditor(true);
                }}
              >
                <PhotoFilterIcon style={{ fontSize: '1.5rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={translate('post_description.bulk_upload', 'Bulk Upload')}>
              <IconButton
                onClick={() => {
                  handleBulkMediaPopup(true);
                }}
              >
                <DriveFolderUploadIcon style={{ fontSize: '1.5rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={translate('post_description.emoji', 'Emoji')}>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <EmojiPicker handleEmoji={handleEmoji}/>
              </Box>
            </Tooltip>
            <Tooltip title={translate('post_description.ai_content', 'AI Content')}>
              <Box
                onClick={() => {
                  handleAIContent(true);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: theme.palette.primary.main,
                  '&:hover': { color: theme.palette.primary.dark },
                }}
              >
                <AIIcon style={{ fontSize: '1.5rem' }} />
                 {!isXlDown && (<Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold' }}>
                  {translate('post_description.free_ai_content_generator', 'Free AI Content Generator')}
                </Typography>)}
              </Box>
            </Tooltip>
            <Tooltip title={translate('post_description.generate_hashtags', 'Generate Hashtags')}>
              <Box
                onClick={handleGenerateHashtags}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: theme.palette.primary.main,
                  '&:hover': { color: theme.palette.primary.dark },
                  ml: 2,
                }}
              >
                <TagIcon style={{ fontSize: '1.5rem' }} />
                 {!isXlDown && (<Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold' }}>
                  {AIHashtagText}
                </Typography>)}
              </Box>
            </Tooltip>
            <Tooltip title={translate('post_description.generate_image', 'Generate Image')}>
              <Box
                onClick={() => {
                  handleImageGeneration();
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: theme.palette.primary.main,
                  '&:hover': { color: theme.palette.primary.dark },
                  ml: 2,
                }}
              >
                <PermMediaIcon style={{ fontSize: '1.5rem' }} />
                 {!isXlDown && (<Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold' }}>
                  {translate('post_description.generate_image', 'Generate Image')}
                </Typography>)}
              </Box>
            </Tooltip>
          </Box>
        )}
      </Box>
    </>
  );
};
export default PostDescription;
